/**
 * @file TaskDetailDialog.tsx
 * @description Окно задачи (phase19.31, SC60; crm.md T1–T7) — заменяет
 * прежнюю карточку активности: вид, срок, исполнитель с «Назначить»,
 * клиент / сделка / заказ ссылками, подробности, история назначений и
 * выполнения; действия — «Выполнено» (исполнитель, автор, владелец),
 * «Вернуть в работу» у выполненной (те же; контракт §13, отказ 403 —
 * текстом) и «Удалить» (автор, с подтверждением второй кнопкой).
 *
 * На телефоне окно на весь экран. Пока идёт одно действие, остальные
 * заблокированы (`busy !== null`): «Выполнено» и «Удалить» одновременно
 * не уходят на сервер.
 */

import { CheckCircle2, RotateCcw, Trash2, TriangleAlert } from "lucide-react";
import { useState, type ReactNode } from "react";
import { Link } from "react-router";

import { Button } from "../../ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../ui/dialog";
import { SectionLabel } from "../../ui/SectionLabel";
import { StatusPill } from "../../ui/StatusPill";
import { ACTIVITY_KIND_LABEL, type ActivityDto } from "../../../lib/api-crm";
import { canAssignTask, canCompleteTask, canDeleteTask, canReopenTask, type TaskViewer } from "./task-permissions";
import { taskGroupKey } from "./task-groups";
import { dayTimeInZone, taskLink } from "./task-texts";
import { TaskAssigneeControl } from "./TaskAssigneeControl";
import { TaskHistoryList } from "./TaskHistoryList";
import { useTaskDetail } from "./useTaskDetail";

interface Props {
  task: ActivityDto;
  viewer: TaskViewer;
  timeZone: string;
  /** База адресов кабинета магазина: `/shop/<slug>`. */
  base: string;
  onClose: () => void;
  /** Задача изменилась — странице перечитать список. */
  onChanged: () => void;
}

/** Строка «подпись — значение». */
function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-1 gap-1 py-2 sm:grid-cols-[8rem_minmax(0,1fr)] sm:items-center sm:gap-3">
      <SectionLabel as="dt">{label}</SectionLabel>
      <dd className="type-body min-w-0 break-words text-on-surface">{children}</dd>
    </div>
  );
}

const LINK_CLASS = "inline-flex min-h-12 items-center text-primary underline-offset-2 hover:underline sm:min-h-0";

export function TaskDetailDialog({ task: initial, viewer, timeZone, base, onClose, onChanged }: Props) {
  const detail = useTaskDetail(initial, onChanged, onClose);
  const { task, busy } = detail;
  const [confirmDelete, setConfirmDelete] = useState(false);
  const overdue = taskGroupKey(task, Date.now(), timeZone) === "overdue";
  const link = taskLink(task);

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent fullScreen className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle className="break-words pr-8">{task.title}</DialogTitle>
          <span className="flex flex-wrap gap-2">
            {task.completedAt ? <StatusPill tone="success">Выполнена</StatusPill> : null}
            {overdue ? <StatusPill tone="attention">Срок прошёл</StatusPill> : null}
          </span>
        </DialogHeader>

        <dl className="flex flex-col divide-y divide-[var(--border)]">
          <Field label="Вид">{ACTIVITY_KIND_LABEL[task.kind]}</Field>
          <Field label="Срок">
            {task.dueAt ? (
              <span className={overdue ? "inline-flex items-center gap-1 font-medium text-[var(--status-attention-fg)]" : "tabular-nums"}>
                {overdue ? <TriangleAlert aria-hidden className="size-3.5" /> : null}
                {dayTimeInZone(task.dueAt, timeZone)}
              </span>
            ) : (
              <span className="text-on-surface-variant">Без срока</span>
            )}
          </Field>
          <Field label="Исполнитель">
            <TaskAssigneeControl
              assigneeUserId={task.assigneeUserId ?? null}
              assigneeName={task.assigneeName ?? null}
              canAssign={canAssignTask(task, viewer)}
              saving={busy === "assign"}
              locked={busy !== null && busy !== "assign"}
              onAssign={detail.assign}
            />
          </Field>
          <Field label="Клиент">
            <Link to={`${base}/customers/${task.customerId}`} className={LINK_CLASS} onClick={onClose}>
              {task.customerName ?? "Карточка клиента"}
            </Link>
          </Field>
          {link ? (
            <Field label={link.kind === "deal" ? "Сделка" : "Заказ"}>
              <Link
                to={link.kind === "deal" ? `${base}/crm/deals/${link.id}` : `${base}/orders/${link.id}`}
                className={LINK_CLASS}
                onClick={onClose}
              >
                {link.kind === "deal" ? task.dealTitle : task.orderNumber}
              </Link>
            </Field>
          ) : null}
          {task.completedAt ? <Field label="Выполнена">{dayTimeInZone(task.completedAt, timeZone)}</Field> : null}
        </dl>

        {task.content ? (
          <section className="flex flex-col gap-1">
            <SectionLabel>Подробности</SectionLabel>
            <p className="type-body whitespace-pre-wrap break-words text-on-surface">{task.content}</p>
          </section>
        ) : null}

        <section className="flex flex-col gap-2">
          <SectionLabel>История</SectionLabel>
          <TaskHistoryList task={task} timeZone={timeZone} />
        </section>

        {/* До 640 — столбиком одинаковой ширины (главное сверху, «Удалить» внизу),
            шире — строкой справа (QA-5 D16: «Выполнено» уезжало отдельно). */}
        <DialogFooter className="sm:flex-wrap">
          {canDeleteTask(task, viewer) ? (
            <Button
              variant={confirmDelete ? "destructive-solid" : "ghost"}
              className="sm:mr-auto"
              loading={busy === "delete"}
              disabled={busy !== null}
              onClick={() => (confirmDelete ? detail.remove() : setConfirmDelete(true))}
            >
              <Trash2 className="size-4" aria-hidden />
              {confirmDelete ? "Точно удалить" : "Удалить"}
            </Button>
          ) : null}
          <Button variant="outline" onClick={onClose}>
            Закрыть
          </Button>
          {canCompleteTask(task, viewer) ? (
            <Button loading={busy === "complete"} disabled={busy !== null} onClick={detail.complete}>
              <CheckCircle2 className="size-4" aria-hidden />
              Выполнено
            </Button>
          ) : null}
          {canReopenTask(task, viewer) ? (
            <Button variant="soft" loading={busy === "reopen"} disabled={busy !== null} onClick={detail.reopen}>
              <RotateCcw className="size-4" aria-hidden />
              Вернуть в работу
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
